<template>
  <component
    :is="element"
    class="button"
    :class="[
      $blockClass(blockClass, 'button'),
      modifierClasses,
      {
        [$class('active')]: showStateIcon,
        'button--parent-hover': parentHover
      },
    ]"
    :href="url || null"
    :type="type || null"
  >
    <span
      class="button__content"
      :class="$blockClass(blockClass, 'button-content')"
    >
      <span
        v-if="showIcon"
        class="button__icon"
        :class="$blockClass(blockClass, 'button-icon')"
      >
        <slot name="icon">
          <icon-arrow-drop-right />
        </slot>
      </span>

      <span
        class="button__label text-body-s"
        :class="[
          $blockClass(blockClass, 'button-label'),
          {
            'text-body-m-desktop': !small,
            'button__label--no-icon': !showIcon
          },
        ]"
        v-text="label"
      />
    </span>

    <span
      v-if="showStateIcon"
      class="button__state"
      :class="$blockClass(blockClass, 'button-state')"
    >
      <slot name="stateIcon" />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Button (btn)
 * -----------------------------------------------------------------------------
 * Global interactive button element.
 * - Styles are imported in critical styles.
 * - Must be imported as <btn> as <button> is a default element.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [customElement] - Optional override to set button element.
 * @param {String} label - Button label.
 * @param {String} [modifiers] - Button modifiers in space separated list
 * (boxed).
 * @param {Boolean} [showIcon] - Whether to show the button icon.
 * @param {Boolean} [showStateIcon] - Show secondary button state, replaces text
 * and default icon with `stateIcon` slot.
 * @param {String} [type] - Type of button (button or submit).
 * @param {String} [url] - URL if link.
 * @param {Boolean} [parentHover] - Triggers the hover state on a parent link
 * hover event.
 * @param {Boolean} [small] - Prevents the desktop styles from being
 * applied.
 *
 */
import IconArrowDropRight from '~icons/direction/arrow-drop/right.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Btn',

  components: {
    IconArrowDropRight,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    customElement: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    modifiers: {
      type: [String, Array],
      default: '',
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showStateIcon: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
      validator: (value) => {
        return ['', 'button', 'submit'].includes(value)
      },
    },
    url: {
      type: String,
      default: null,
    },
    parentHover: {
      type: Boolean,
      default: false,
    },

    small: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return this.url ? 'a' : 'button'
    },

    /**
     * Compute modifier classes.
     * @returns {String}
     */
    modifierClasses() {
      if (!this.modifiers) {
        return ''
      }

      let modifiers = []

      if (Array.isArray(this.modifiers)) {
        modifiers = this.modifiers
      } else {
        modifiers = this.modifiers.split(/[\s,]/g)
      }

      return modifiers.map((modifier) => {
        return `button--${modifier}`
      }).join(' ')
    },
  },
}
</script>
